/**
 * Popisek jednoho B2C presetu — sdílený zákaznickým konfigurátorem
 * (``TunablePresetsSection``) a admin náhledem (``ModuleConfigForm``), aby
 * tenant viděl přesně ten text, který uvidí zákazník.
 *
 * Holá hodnota bez jednotky zákazníkovi nic neřekne — „10 / 20 / 25" u výšky
 * vrstvy nerozliší mm od µm ani od procent (KAN-1041). Popisek proto vždycky
 * nese hodnotu v jednotce dimenze, lokalizovaně; pojmenoval-li tenant preset
 * (``label_cs`` / ``label_en``), hodnota se připojí ZA název, ne místo něj.
 */

import { resolveI18nText } from "@/components/admin/I18nField";

/** Počet desetinných míst zapsaných v hodnotě (2 pro 0.25, 0 pro 20). */
function decimalPlaces(value: number): number {
  const dot = String(value).indexOf(".");
  return dot < 0 ? 0 : String(value).length - dot - 1;
}

/**
 * Hodnota presetu s jednotkou, zarovnaná na nejjemnější preset v nabídce:
 * ``[0.1, 0.2, 0.25]`` dá „0,10 mm" / „0,20 mm" / „0,25 mm", takže se řada
 * čte jako jedna stupnice, ne jako tři různě přesná čísla.
 */
export function formatPresetValue(
  value: number,
  lang: string,
  unit?: string,
  siblings?: readonly number[],
): string {
  const digits = Math.min(
    6,
    Math.max(decimalPlaces(value), ...(siblings ?? []).map(decimalPlaces)),
  );
  const text = new Intl.NumberFormat(lang, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
  return unit ? `${text} ${unit}` : text;
}

/** Preset tak, jak přijde z ``b2c_presets`` (tenantská data, volné klíče). */
export interface PresetRow {
  value?: unknown;
  label_cs?: unknown;
  label_en?: unknown;
}

/** „0,20 mm" bez názvu, „Standardní — 0,20 mm" s názvem. */
export function presetLabel(
  row: PresetRow,
  lang: string,
  unit?: string,
  siblings?: readonly number[],
): string {
  const cs = typeof row.label_cs === "string" ? row.label_cs : undefined;
  const en = typeof row.label_en === "string" ? row.label_en : undefined;
  const name = resolveI18nText({ cs, en }, lang);
  const value =
    typeof row.value === "number" && Number.isFinite(row.value)
      ? formatPresetValue(row.value, lang, unit, siblings)
      : String(row.value ?? "");
  if (!name) return value;
  // Tenant, který preset pojmenoval hodnotou („20"), by jinak dostal
  // „20 — 20 %" — název nesoucí totéž co hodnota se nezdvojuje.
  if (!value || name === value) return name;
  return `${name} — ${value}`;
}
